import type { ApprovalStaggeredTime } from "../../../../api/work-policy-application-api.types";
import {
  CountBadge,
  SurfaceIntro,
  SurfaceState,
  TimeSelectField,
  TimeSummary
} from "./work-policy-application.styles";

type StaggeredStatus = "loading" | "ready" | "error";

interface StaggeredApplicationFieldsProps {
  status: StaggeredStatus;
  options: ApprovalStaggeredTime[];
  value: string | null;
  onChange: (startTime: string) => void;
  onRetry: () => void;
}

function formatMinutes(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${remainder}분`;
  return remainder === 0 ? `${hours}시간` : `${hours}시간 ${remainder}분`;
}

export function StaggeredApplicationFields({
  status,
  options,
  value,
  onChange,
  onRetry
}: StaggeredApplicationFieldsProps) {
  if (status === "loading") {
    return <SurfaceState role="status">허용 시간을 불러오는 중입니다.</SurfaceState>;
  }
  if (status === "error") {
    return (
      <SurfaceState role="alert">
        <span>허용 시간을 불러오지 못했습니다.</span>
        <button type="button" onClick={onRetry}>다시 시도</button>
      </SurfaceState>
    );
  }
  if (options.length === 0) {
    return (
      <SurfaceState role="alert">
        <span>현재 선택 가능한 출근 시간이 없습니다. 다른 정책을 선택해 주세요.</span>
      </SurfaceState>
    );
  }
  const selected = options.find(option => option.startTime === value);
  if (value && !selected) {
    return (
      <SurfaceState role="alert">
        <span>선택했던 시간이 더 이상 허용되지 않는 시간대입니다.</span>
        <button type="button" onClick={onRetry}>허용 시간 다시 불러오기</button>
      </SurfaceState>
    );
  }
  return (
    <>
      <SurfaceIntro>
        <h4>출퇴근 시간을 선택해 주세요</h4>
        <p>선택한 시간대는 신청 기간 전체에 적용됩니다.</p>
        <CountBadge>선택 가능 {options.length}개</CountBadge>
      </SurfaceIntro>
      <TimeSelectField htmlFor="work-policy-staggered-time">
        근무 시간대
        <select
          id="work-policy-staggered-time"
          value={value ?? ""}
          onChange={event => {
            const option = options.find(item => item.startTime === event.currentTarget.value);
            if (option) onChange(option.startTime);
          }}
        >
          <option value="">근무 시간대를 선택해 주세요</option>
          {options.map(option => (
            <option key={option.staggeredTimeId} value={option.startTime}>
              {option.startTime.slice(0, 5)} 출근 ~ {option.expectedEndTime.slice(0, 5)} 퇴근
            </option>
          ))}
        </select>
      </TimeSelectField>
      {selected && (
        <TimeSummary aria-label="선택한 근무 시간 요약">
          <div>
            <small>출근 → 퇴근</small>
            <strong>{selected.startTime.slice(0, 5)} → {selected.expectedEndTime.slice(0, 5)}</strong>
          </div>
          <div>
            <small>휴게시간</small>
            <strong>휴게시간 {formatMinutes(selected.breakMinutes)}</strong>
          </div>
          <div>
            <small>실근로시간</small>
            <strong>실근로시간 {formatMinutes(selected.workMinutes)}</strong>
          </div>
        </TimeSummary>
      )}
    </>
  );
}
